/**
 * Pricing admin write-surface error vocabulary — B2.5a' (fullsuite plan
 * §B2.5a', spec-by-mock-first).
 *
 * ONE code list shared VERBATIM by the three write callers:
 *   - the MSW handlers (`mocks/handlers/pricing.ts`) — HTTP status via
 *     `PRICING_ADMIN_ERROR_STATUS`;
 *   - the FE store (`src/stores/pricingStore.ts`) — throws these codes;
 *   - the GraphQL server (`server/src/modules/pricing/**`) — maps each code
 *     to a `userError` (errors-as-data), never an HTTP status.
 *
 * The first five codes are the S9.1/S4.1 store vocabulary carried VERBATIM
 * (`PricingGuardCode`); the rest are the B2.5a' additions the write surface
 * needs once saves stop being store-local (band geometry, code uniqueness,
 * validity windows, percent ranges, the read-only purchase cost, and the
 * write-once snapshot).
 *
 * i18n: every code has an `errors.pricingAdmin.<CODE>` key in BOTH
 * `src/i18n/locales/en/pricing.json` and `.../zh/pricing.json` — the symmetric
 * pair is asserted by `npm run i18n:audit`; `pricingAdminErrorKey` is the ONE
 * spelling of that key so no caller hand-builds it.
 */

/** Every rejection the pricing admin write surface can produce. */
export type PricingAdminErrorCode =
  // --- carried verbatim from the S9.1/S4.1 store guard vocabulary ---
  | 'REASON_REQUIRED'
  | 'RULE_NOT_FOUND'
  | 'RECORD_NOT_FOUND'
  | 'FIXED_PRICE_LOCKED'
  | 'SCHEDULE_EITHER_OR'
  // --- B2.5a' additions (writes leave the store) ---
  | 'BAND_RANGE_INVALID'
  | 'BAND_OVERLAP'
  | 'LEVEL_CODE_TAKEN'
  | 'LEVEL_NOT_FOUND'
  | 'GROUP_CODE_TAKEN'
  | 'GROUP_NOT_FOUND'
  | 'SCHEDULE_CODE_TAKEN'
  | 'BRACKETS_REQUIRED'
  | 'BRACKET_DUPLICATE'
  | 'PERCENT_RANGE'
  | 'PRICE_RANGE'
  | 'VALIDITY_WINDOW_INVALID'
  | 'PURCHASE_COST_READONLY'
  | 'SNAPSHOT_IMMUTABLE'
  | 'VALIDATION';

export const PRICING_ADMIN_ERROR_CODES: readonly PricingAdminErrorCode[] = [
  'REASON_REQUIRED',
  'RULE_NOT_FOUND',
  'RECORD_NOT_FOUND',
  'FIXED_PRICE_LOCKED',
  'SCHEDULE_EITHER_OR',
  'BAND_RANGE_INVALID',
  'BAND_OVERLAP',
  'LEVEL_CODE_TAKEN',
  'LEVEL_NOT_FOUND',
  'GROUP_CODE_TAKEN',
  'GROUP_NOT_FOUND',
  'SCHEDULE_CODE_TAKEN',
  'BRACKETS_REQUIRED',
  'BRACKET_DUPLICATE',
  'PERCENT_RANGE',
  'PRICE_RANGE',
  'VALIDITY_WINDOW_INVALID',
  'PURCHASE_COST_READONLY',
  'SNAPSHOT_IMMUTABLE',
  'VALIDATION',
] as const;

/**
 * Machine-readable guard failure. Carries the offending input path when the
 * surface can point at one (`input.markupPct`) — the GraphQL layer forwards it
 * as `UserError.field`, the MSW layer as the `field` member of the error body.
 */
export class PricingAdminError extends Error {
  readonly code: PricingAdminErrorCode;
  readonly field: string | null;

  constructor(code: PricingAdminErrorCode, field: string | null = null) {
    super(code);
    this.name = 'PricingAdminError';
    this.code = code;
    this.field = field;
  }
}

/** Narrowing helper (cross-realm safe — instanceof alone is not). */
export function isPricingAdminError(value: unknown): value is PricingAdminError {
  return (
    value instanceof Error &&
    (value as PricingAdminError).name === 'PricingAdminError' &&
    typeof (value as PricingAdminError).code === 'string'
  );
}

/**
 * HTTP status for the MSW seam. 409 = a conflict with EXISTING data the caller
 * must resolve first (an overlapping band, a taken code, a locked price);
 * 404 = the referenced row does not exist; everything else is a 400 malformed
 * or refused write. The GraphQL server does NOT use this map — it answers with
 * errors-as-data payloads at HTTP 200 (kernel convention).
 */
export const PRICING_ADMIN_ERROR_STATUS: Record<PricingAdminErrorCode, number> = {
  REASON_REQUIRED: 400,
  RULE_NOT_FOUND: 404,
  RECORD_NOT_FOUND: 404,
  FIXED_PRICE_LOCKED: 409,
  SCHEDULE_EITHER_OR: 400,
  BAND_RANGE_INVALID: 400,
  BAND_OVERLAP: 409,
  LEVEL_CODE_TAKEN: 409,
  LEVEL_NOT_FOUND: 404,
  GROUP_CODE_TAKEN: 409,
  GROUP_NOT_FOUND: 404,
  SCHEDULE_CODE_TAKEN: 409,
  BRACKETS_REQUIRED: 400,
  BRACKET_DUPLICATE: 400,
  PERCENT_RANGE: 400,
  PRICE_RANGE: 400,
  VALIDITY_WINDOW_INVALID: 400,
  PURCHASE_COST_READONLY: 400,
  SNAPSHOT_IMMUTABLE: 409,
  VALIDATION: 400,
};

/**
 * The ONE i18n key spelling for a pricing admin rejection (en + zh).
 *
 * House shape `pricing:pricing.<section>.<leaf>` (see
 * `PriceTracePanel.tsx`'s skipReason map). The leaf is the CODE VERBATIM
 * rather than a camelCase alias: a second naming table between the machine
 * vocabulary and the copy is one more place they can drift, and a missing key
 * then fails loudly in `npm run i18n:audit` instead of resolving to a wrong
 * sentence.
 */
export function pricingAdminErrorKey(code: PricingAdminErrorCode): string {
  return `pricing:pricing.adminErrors.${code}`;
}
